import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { t } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Star, Truck, ShieldCheck, Package } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";

export default function ProductDetail() {
  const { id } = useParams();
  const { lang, addToCart, formatPrice, user } = useStore();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [qty, setQty] = useState(1);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [reviewerName, setReviewerName] = useState("");

  const loadReviews = () => api.get("/reviews", { params: { product_id: id } }).then((r) => setReviews(r.data));

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setProduct(r.data));
    loadReviews();
  }, [id]);

  const submitReview = async () => {
    if (!comment.trim()) return toast.error(lang === "ar" ? "يرجى كتابة تعليق" : "Please write a comment");
    try {
      await api.post("/reviews", {
        product_id: id,
        rating,
        comment,
        reviewer_name: reviewerName || (user?.name) || (lang === "ar" ? "زائر" : "Guest"),
      });
      setComment("");
      setReviewerName("");
      setRating(5);
      toast.success(lang === "ar" ? "شكراً لتقييمك!" : "Thanks for your review!");
      await loadReviews();
      const p = await api.get(`/products/${id}`);
      setProduct(p.data);
    } catch (e) {
      toast.error("Error");
    }
  };

  if (!product) return <div className="min-h-[60vh] flex items-center justify-center">Loading…</div>;
  const name = lang === "ar" ? product.name_ar : product.name_en;
  const desc = lang === "ar" ? product.description_ar : product.description_en;

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-8 py-10 md:py-14">
      <div className="grid md:grid-cols-2 gap-10">
        <div className="bg-[#faf3e3] rounded-2xl overflow-hidden aspect-square">
          <img src={product.image} alt={name} className="w-full h-full object-cover" />
        </div>
        <div>
          <h1 className={`${lang === "ar" ? "font-arabic-heading" : "font-english-heading"} text-3xl md:text-4xl font-semibold mb-3`} style={{ color: "var(--thiqa-primary)" }}>
            {name}
          </h1>
          <div className="flex items-center gap-1 mb-4">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} size={16} className={n <= Math.round(product.rating_avg || 0) ? "star-fill fill-current" : "star-empty"} />
            ))}
            <span className="text-sm text-gray-500 ms-2">({product.rating_count || 0} {t(lang, "reviews")})</span>
          </div>
          <p className="text-gray-700 mb-6 leading-relaxed">{desc}</p>
          <div className="text-4xl font-bold mb-6" style={{ color: "var(--thiqa-primary)" }}>{formatPrice(product.price_usd)}</div>
          <div className="flex items-center gap-3 mb-6">
            <div className="flex items-center border border-gray-300 rounded-full">
              <button data-testid="qty-decrease" onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4 py-2">−</button>
              <span className="px-4">{qty}</span>
              <button data-testid="qty-increase" onClick={() => setQty((q) => q + 1)} className="px-4 py-2">+</button>
            </div>
            <Button
              data-testid="detail-add-to-cart"
              onClick={() => { addToCart(product, qty); toast.success(lang === "ar" ? "أُضيف إلى السلة" : "Added to cart"); }}
              className="btn-primary-thiqa rounded-full px-8 py-6 flex-1"
            >
              {t(lang, "add_to_cart")}
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-6 pt-6 border-t border-gray-200">
            <div className="flex flex-col items-center text-center gap-1"><Truck size={20} style={{ color: "var(--thiqa-gold)" }} /><span className="text-xs text-gray-600">{t(lang, "est_delivery")}</span></div>
            <div className="flex flex-col items-center text-center gap-1"><ShieldCheck size={20} style={{ color: "var(--thiqa-gold)" }} /><span className="text-xs text-gray-600">{t(lang, "secure_pay")}</span></div>
            <div className="flex flex-col items-center text-center gap-1"><Package size={20} style={{ color: "var(--thiqa-gold)" }} /><span className="text-xs text-gray-600">{lang === "ar" ? "شحنة موحدة" : "Consolidated"}</span></div>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <div className="mt-16">
        <h2 className={`${lang === "ar" ? "font-arabic-heading" : "font-english-heading"} text-2xl md:text-3xl font-semibold mb-6`} style={{ color: "var(--thiqa-primary)" }}>
          {t(lang, "customer_reviews")}
        </h2>
        <div className="bg-white border border-[#e8ddc9] rounded-2xl p-6 mb-8">
          <h3 className="font-semibold mb-4">{t(lang, "write_review")}</h3>
          <div className="flex items-center gap-1 mb-4">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                data-testid={`rate-star-${n}`}
                onClick={() => setRating(n)}
                aria-label={`Rate ${n}`}
              >
                <Star size={26} className={n <= rating ? "star-fill fill-current" : "star-empty"} />
              </button>
            ))}
          </div>
          {!user && (
            <Input
              data-testid="review-name"
              placeholder={t(lang, "name")}
              value={reviewerName}
              onChange={(e) => setReviewerName(e.target.value)}
              className="mb-3"
            />
          )}
          <Textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t(lang, "comment")} rows={3} />
          <Button data-testid="review-submit" onClick={submitReview} className="btn-primary-thiqa rounded-full mt-4">{t(lang, "submit")}</Button>
        </div>

        {reviews.length === 0 ? (
          <p className="text-gray-500">{t(lang, "no_reviews")}</p>
        ) : (
          <div className="space-y-4">
            {reviews.map((r) => {
              const avatar = r.reviewer_avatar
                ? (r.reviewer_avatar.startsWith("http") ? r.reviewer_avatar : `${process.env.REACT_APP_BACKEND_URL}${r.reviewer_avatar}`)
                : null;
              return (
                <div key={r.id} className="bg-white border border-[#e8ddc9] rounded-xl p-5" data-testid={`review-${r.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-[#f5efe4] flex items-center justify-center border border-[#e8ddc9]">
                        {avatar ? (
                          <img src={avatar} alt={r.reviewer_name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-sm font-semibold" style={{ color: "var(--thiqa-primary)" }}>
                            {(r.reviewer_name || "?").charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div>
                        <div className="font-semibold text-sm">{r.reviewer_name}</div>
                        {r.user_id && <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--thiqa-gold)" }}>{lang === "ar" ? "عميل موثّق" : "Verified"}</div>}
                      </div>
                    </div>
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map((n) => <Star key={n} size={14} className={n <= r.rating ? "star-fill fill-current" : "star-empty"} />)}
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed">{r.comment}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
